import { SwapOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { appActions } from 'reduxStore/app/slice';
import { selectActiveProducts } from 'reduxStore/settings/selectors';
import { Flex, Typography } from 'zui/Antd';
import { Logo } from 'zui/Custom';
import { StyledDropDown } from './styles';

const { Text, Title } = Typography;

const SwitchOrganisationDropdown = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const {
		token: {
			colorTextLabel,
			colorBgContainer,
			colorBorder,
			borderRadiusLG,
			boxShadow,
			colorTextPlaceholder,
			marginMD,
			paddingXS,
			lineHeight,
			fontSizeSM
		}
	} = theme.useToken();

	const {
		collapsed,
		dashboard: { selected }
	} = useSelector((state) => state.App);

	const activeProducts = useSelector(selectActiveProducts);

	// Products available to switch TO (everything except the one we're on)
	const switchableProducts = activeProducts.filter((p) => p.key !== selected);

	// Current product — fallback to first active in rare transitional states.
	const currentProduct = activeProducts.find((p) => p.key === selected) ?? activeProducts[0];

	// Disabled when there's nowhere to switch to. We still render the same
	// wrapper so the header layout (padding, background, height) is preserved —
	// only the dropdown behavior and the swap icon are turned off.
	const isDisabled = switchableProducts.length === 0;

	const handleSwitch = (product) => {
		if (product.kind === 'external') {
			// External products (e.g. Rooms) — open in their own context.
			// Redux.dashboard.selected is not updated because the user leaves this SPA.
			product.open?.();
			return;
		}

		dispatch(appActions.switchDashboard(product.key));
		dispatch(appActions.changeCurrent(['']));
		navigate(product.route);
	};

	const renderItem = (product) => (
		<Flex
			gap={15}
			onClick={() => handleSwitch(product)}
			style={{
				padding: `${paddingXS}px 0px`,
				textWrap: 'wrap'
			}}
		>
			<Flex>{product.logo}</Flex>
			<Flex vertical gap={6}>
				<Title style={{ margin: 0 }} level={5}>
					{product.label}
				</Title>
				<Text style={{ lineHeight: lineHeight, fontSize: fontSizeSM }} ellipsis={{ expanded: true }}>
					{product.description}
				</Text>
			</Flex>
		</Flex>
	);

	const items = switchableProducts.map((product) => ({
		key: product.key,
		label: renderItem(product)
	}));

	return (
		<StyledDropDown
			arrow={false}
			menu={{ items }}
			placement='right'
			colorBorder={colorBorder}
			// Empty trigger array disables open-on-click — no dropdown appears.
			// Cleaner than `disabled` which can affect wrapper styling.
			trigger={isDisabled ? [] : ['click']}
			dropdownRender={(menu) => (
				<Flex
					style={{
						borderRadius: borderRadiusLG,
						backgroundColor: colorBgContainer,
						boxShadow: boxShadow,
						margin: `10px ${marginMD}px`,
						maxWidth: 377,
						padding: '27px 15px',
						zIndex: 99
					}}
					vertical
					gap={28}
				>
					<Title level={5} style={{ margin: 0, color: colorTextPlaceholder }}>
						Switch Dashboard
					</Title>
					{React.cloneElement(menu, { style: { padding: 0, boxShadow: 'none' } })}
				</Flex>
			)}
		>
			<Flex
				justify={!collapsed ? 'space-between' : 'center'}
				style={{
					backgroundColor: colorBgContainer,
					height: 55,
					cursor: isDisabled ? 'default' : 'pointer'
				}}
			>
				<Logo product={currentProduct?.key ?? 'zezamii'} />
				{!collapsed && !isDisabled && <SwapOutlined style={{ color: colorTextLabel }} />}
			</Flex>
		</StyledDropDown>
	);
};

export default SwitchOrganisationDropdown;
